import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'localization';
import CustomDialogAtom from '../../atoms/CustomDialog/CustomDialogAtom';
import MessageAtom from '../../atoms/Message/MessageAtom';
import SpinnerAtom from '../../atoms/Spinner/SpinnerAtom';
import { confirmDialogAtom } from '../../atoms/ConfirmDialog/ConfirmDialogAtom';
import {
  INLINE_PREVIEW_ALLOWLIST,
  type SphereAttachment,
} from 'types/attachment';
import { useSphereAttachments } from '@center-pwa/hooks/useSphereAttachments';
import {
  getAttachmentSignedUrl,
  type UpdateLinkAttachmentPayload,
} from '@center-pwa/api/attachmentsApi';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import AttachmentTileAtom from '../../atoms/AttachmentTile/AttachmentTileAtom';
import AttachmentPreviewMolecule from '../AttachmentPreview/AttachmentPreviewMolecule';
import AddLinkFormPart, {
  type AddLinkFormSubmitPayload,
} from '../AddLinkDialog/AddLinkFormPart';
import './AttachmentsGridMolecule.scss';

const IMAGE_THUMBNAIL_MIMES: ReadonlySet<string> = new Set([
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
]);

export interface AttachmentsGridMoleculeProps {
  sphereId: string;
  canEdit: boolean;
  isVisible?: boolean;
  attachmentsEnabled?: boolean;
  className?: string;
}

export interface AttachmentsGridMoleculeViewProps {
  canEdit: boolean;
  attachments: SphereAttachment[];
  isLoading: boolean;
  error: string | null;
  onAddFile: (file: File) => Promise<void>;
  onAddLink: (payload: AddLinkFormSubmitPayload) => Promise<void>;
  onUpdateLink: (
    attachmentId: string,
    payload: UpdateLinkAttachmentPayload,
  ) => Promise<void>;
  onRemove: (attachmentId: string) => Promise<void>;
  fetchSignedUrl?: (attachmentId: string) => Promise<string>;
  className?: string;
}

export const AttachmentsGridMoleculeView: React.FC<
  AttachmentsGridMoleculeViewProps
> = ({
  canEdit,
  attachments,
  isLoading,
  error,
  onAddFile,
  onAddLink,
  onUpdateLink,
  onRemove,
  fetchSignedUrl,
  className = '',
}) => {
  const { t } = useTranslation();
  const linkFormId = useId();

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const inFlightThumbnailIdsRef = useRef<Set<string>>(new Set());

  const [isLinkFormOpen, setIsLinkFormOpen] = useState(false);
  const [editingAttachmentId, setEditingAttachmentId] = useState<string | null>(
    null,
  );
  const [isDragOver, setIsDragOver] = useState(false);
  const [previewAttachment, setPreviewAttachment] =
    useState<SphereAttachment | null>(null);
  const [thumbnails, setThumbnails] = useState<Map<string, string>>(
    () => new Map(),
  );

  const editingAttachment = useMemo(
    () =>
      editingAttachmentId
        ? (attachments.find((a) => a.id === editingAttachmentId) ?? null)
        : null,
    [attachments, editingAttachmentId],
  );

  useEffect(() => {
    if (editingAttachmentId && !editingAttachment) {
      setEditingAttachmentId(null);
    }
  }, [editingAttachmentId, editingAttachment]);

  useEffect(() => {
    if (!fetchSignedUrl) return;
    const candidates = attachments.filter(
      (a) =>
        a.attachment_type === 'file' &&
        a.scan_status === 'clean' &&
        a.mime_type !== null &&
        IMAGE_THUMBNAIL_MIMES.has(a.mime_type) &&
        !thumbnails.has(a.id) &&
        !inFlightThumbnailIdsRef.current.has(a.id),
    );
    if (candidates.length === 0) return;

    let cancelled = false;
    candidates.forEach((attachment) => {
      inFlightThumbnailIdsRef.current.add(attachment.id);
    });

    void Promise.all(
      candidates.map(async (attachment) => {
        try {
          const url = await fetchSignedUrl(attachment.id);
          return { id: attachment.id, url };
        } catch {
          return null;
        } finally {
          inFlightThumbnailIdsRef.current.delete(attachment.id);
        }
      }),
    ).then((results) => {
      if (cancelled) return;
      const fresh = results.filter(
        (r): r is { id: string; url: string } => r !== null,
      );
      if (fresh.length === 0) return;
      setThumbnails((prev) => {
        const next = new Map(prev);
        fresh.forEach(({ id, url }) => next.set(id, url));
        return next;
      });
    });

    return () => {
      cancelled = true;
    };
  }, [attachments, fetchSignedUrl, thumbnails]);

  const closeLinkForm = useCallback(() => setIsLinkFormOpen(false), []);

  // Document-level Escape handler so the form containers don't need to be
  // interactive elements themselves (Sonar S6848 — non-interactive elements
  // shouldn't carry keyboard handlers). One global listener is cheap and
  // captures Escape regardless of where focus sits inside the form.
  useEffect(() => {
    if (!isLinkFormOpen) return undefined;
    const onEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        closeLinkForm();
      }
    };
    document.addEventListener('keydown', onEscape);
    return () => document.removeEventListener('keydown', onEscape);
  }, [isLinkFormOpen, closeLinkForm]);

  const handleStartEdit = useCallback((attachmentId: string) => {
    setEditingAttachmentId(attachmentId);
    setIsLinkFormOpen(false);
  }, []);

  const handleEditCancel = useCallback(() => {
    setEditingAttachmentId(null);
  }, []);

  // Mirror of the link-form Escape handler above for the edit form.
  useEffect(() => {
    if (!editingAttachment) return undefined;
    const onEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        handleEditCancel();
      }
    };
    document.addEventListener('keydown', onEscape);
    return () => document.removeEventListener('keydown', onEscape);
  }, [editingAttachment, handleEditCancel]);

  const handleEditSubmit = useCallback(
    async (payload: AddLinkFormSubmitPayload) => {
      if (!editingAttachment) return;
      const diff: UpdateLinkAttachmentPayload = {};

      if (payload.url !== editingAttachment.url) {
        diff.url = payload.url;
      }

      const currentName = editingAttachment.name ?? '';
      if (payload.name !== undefined && payload.name !== currentName) {
        diff.name = payload.name;
      }

      const currentDescription = editingAttachment.description ?? null;
      const submittedDescription = payload.description ?? null;
      if (submittedDescription !== currentDescription) {
        diff.description = submittedDescription;
      }

      if (Object.keys(diff).length === 0) {
        setEditingAttachmentId(null);
        return;
      }

      await onUpdateLink(editingAttachment.id, diff);
      setEditingAttachmentId(null);
    },
    [editingAttachment, onUpdateLink],
  );

  const uploadFiles = useCallback(
    async (files: File[]) => {
      for (const file of files) {
        try {
          await onAddFile(file);
        } catch {
          // Each file independent; surface to hook-level error state.
        }
      }
    },
    [onAddFile],
  );

  const handleUploadButtonClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileInputChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(event.target.files ?? []);
      event.target.value = '';
      if (files.length === 0) return;
      await uploadFiles(files);
    },
    [uploadFiles],
  );

  const handleLinkButtonClick = useCallback(() => {
    setEditingAttachmentId(null);
    setIsLinkFormOpen((prev) => !prev);
  }, []);

  const handleLinkSubmit = useCallback(
    async (payload: AddLinkFormSubmitPayload) => {
      await onAddLink(payload);
      setIsLinkFormOpen(false);
    },
    [onAddLink],
  );

  const handleDragOver = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      if (!canEdit) return;
      event.preventDefault();
      event.stopPropagation();
      setIsDragOver(true);
    },
    [canEdit],
  );

  const handleDragLeave = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      if (event.currentTarget.contains(event.relatedTarget as Node | null)) {
        return;
      }
      setIsDragOver(false);
    },
    [],
  );

  const handleDrop = useCallback(
    async (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setIsDragOver(false);
      if (!canEdit) return;
      setIsLinkFormOpen(false);
      setEditingAttachmentId(null);
      const files = Array.from(event.dataTransfer?.files ?? []);
      if (files.length === 0) return;
      await uploadFiles(files);
    },
    [canEdit, uploadFiles],
  );

  const triggerDownload = useCallback(
    async (attachment: SphereAttachment) => {
      if (!fetchSignedUrl) return;
      try {
        const url = await fetchSignedUrl(attachment.id);
        window.open(url, '_blank', 'noopener,noreferrer');
      } catch {
        // No-op: surface via hook error if needed.
      }
    },
    [fetchSignedUrl],
  );

  const handleTileClick = useCallback(
    (attachment: SphereAttachment) => {
      if (attachment.scan_status === 'pending') return;
      if (
        attachment.scan_status === 'malicious' ||
        attachment.scan_status === 'quarantined'
      ) {
        return;
      }
      if (attachment.attachment_type === 'link') {
        if (attachment.url) {
          window.open(attachment.url, '_blank', 'noopener,noreferrer');
        }
        return;
      }
      const mime = attachment.mime_type ?? '';
      if (INLINE_PREVIEW_ALLOWLIST.has(mime)) {
        setPreviewAttachment(attachment);
        return;
      }
      triggerDownload(attachment).catch(() => undefined);
    },
    [triggerDownload],
  );

  const handleTileDelete = useCallback(
    (attachment: SphereAttachment) => {
      setEditingAttachmentId(null);
      const displayName = attachment.name?.trim() || t('attachments.unnamed');
      confirmDialogAtom({
        message: t('attachments.confirmDelete.message', { name: displayName }),
        header: t('attachments.confirmDelete.title'),
        icon: 'pi pi-exclamation-triangle',
        variant: 'danger',
        acceptLabel: t('attachments.confirmDelete.confirm'),
        rejectLabel: t('attachments.confirmDelete.cancel'),
        accept: () => {
          onRemove(attachment.id).catch(() => undefined);
        },
      });
    },
    [onRemove, t],
  );

  const handleClosePreview = useCallback(() => {
    setPreviewAttachment(null);
  }, []);

  const count = attachments.length;
  const hasAttachments = count > 0;
  const titleLabel = hasAttachments
    ? `${t('attachments.sectionTitle')} (${count})`
    : t('attachments.sectionTitle');

  const rootClasses = ['attachments-grid-molecule', className]
    .filter(Boolean)
    .join(' ');

  const panelClasses = [
    'attachments-grid-molecule__panel',
    isDragOver ? 'attachments-grid-molecule__panel--drag-over' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={rootClasses}
      data-testid="attachments-grid-molecule"
    >
      <div className="attachments-grid-molecule__header">
        <h3
          className="attachments-grid-molecule__title"
          data-testid="attachments-grid-title"
        >
          {titleLabel}
        </h3>
        {canEdit && (
          <div className="attachments-grid-molecule__actions">
            <ButtonAtom
              type="button"
              variant="primary"
              icon="pi pi-upload"
              label={t('attachments.actions.upload')}
              onClick={handleUploadButtonClick}
              data-testid="attachments-grid-upload-button"
            />
            <ButtonAtom
              type="button"
              variant="text"
              icon="pi pi-link"
              label={t('attachments.actions.link')}
              onClick={handleLinkButtonClick}
              aria-expanded={isLinkFormOpen}
              aria-controls={linkFormId}
              data-testid="attachments-grid-link-button"
            />
          </div>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="attachments-grid-molecule__file-input"
        onChange={handleFileInputChange}
        aria-hidden="true"
        tabIndex={-1}
        data-testid="attachments-grid-file-input"
      />

      {isLinkFormOpen && !editingAttachment && (
        <div
          id={linkFormId}
          className="attachments-grid-molecule__link-form"
          data-testid="attachments-grid-link-form"
        >
          <AddLinkFormPart
            onSubmit={handleLinkSubmit}
            onCancel={closeLinkForm}
          />
        </div>
      )}

      {editingAttachment && (
        <div
          className="attachments-grid-molecule__link-form"
          data-testid="attachments-grid-edit-form"
          data-editing-attachment-id={editingAttachment.id}
        >
          <AddLinkFormPart
            mode="edit"
            initialValues={{
              url: editingAttachment.url ?? '',
              name: editingAttachment.name ?? '',
              description: editingAttachment.description,
            }}
            onSubmit={handleEditSubmit}
            onCancel={handleEditCancel}
          />
        </div>
      )}

      <section
        className={panelClasses}
        onDragOver={handleDragOver}
        onDragEnter={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        aria-label={t('attachments.sectionTitle')}
        data-testid="attachments-grid-panel"
      >
        {error && (
          <MessageAtom
            severity="error"
            text={t('attachments.loadFailed')}
            className="attachments-grid-molecule__error"
          />
        )}

        {(() => {
          if (isLoading) {
            return (
              <output
                className="attachments-grid-molecule__loading"
                data-testid="attachments-grid-loading"
              >
                <SpinnerAtom size="normal" />
              </output>
            );
          }
          if (hasAttachments) {
            return (
              <ul
                className="attachments-grid-molecule__grid"
                data-testid="attachments-grid-list"
              >
                {attachments.map((attachment) => {
                  const isEditing = attachment.id === editingAttachmentId;
                  const canEditThisLink =
                    canEdit && attachment.attachment_type === 'link';
                  return (
                    <li key={attachment.id} className="attachments-grid-molecule__grid-item">
                      <AttachmentTileAtom
                        attachment={attachment}
                        thumbnailUrl={thumbnails.get(attachment.id)}
                        canDelete={canEdit}
                        onClick={() => handleTileClick(attachment)}
                        onDelete={
                          canEdit
                            ? () => handleTileDelete(attachment)
                            : undefined
                        }
                        onEdit={
                          canEditThisLink
                            ? () => handleStartEdit(attachment.id)
                            : undefined
                        }
                        className={
                          isEditing
                            ? 'attachments-grid-molecule__tile--editing'
                            : undefined
                        }
                      />
                    </li>
                  );
                })}
              </ul>
            );
          }
          return (
            <div
              className="attachments-grid-molecule__empty"
              data-testid="attachments-grid-empty"
            >
              <i className="pi pi-paperclip attachments-grid-molecule__empty-icon" aria-hidden="true" />
              <p className="attachments-grid-molecule__empty-text">
                {canEdit
                  ? t('attachments.empty')
                  : t('attachments.emptyReadOnly')}
              </p>
              {canEdit && (
                <p className="attachments-grid-molecule__empty-hint">
                  {t('attachments.emptyHint', 'Drag files here or use Upload')}
                </p>
              )}
            </div>
          );
        })()}

        {isDragOver && canEdit && (
          <div
            className="attachments-grid-molecule__drop-overlay"
            aria-hidden="true"
          >
            <i className="pi pi-cloud-upload" />
            <span>{t('attachments.dropToUpload')}</span>
          </div>
        )}
      </section>

      {previewAttachment && (
        <CustomDialogAtom
          visible
          header={previewAttachment.name?.trim() || t('attachments.unnamed')}
          onHide={handleClosePreview}
          className="attachments-grid-molecule__preview-dialog"
        >
          <AttachmentPreviewMolecule
            attachment={previewAttachment}
            fetchSignedUrl={fetchSignedUrl}
          />
        </CustomDialogAtom>
      )}
      {/*
        No <ConfirmDialogAtom /> here. PrimeReact's imperative
        confirmDialog({...}) (which our `confirmDialogAtom` wraps)
        broadcasts to every mounted ConfirmDialog slot — if this molecule
        renders one AND an ancestor (SphereEditor) renders one, both fire and
        the user's accept click runs the DELETE handler twice (the second call
        always 404s because the row is already gone). The ancestor's
        slot handles our imperative calls.
      */}
    </div>
  );
};

const AttachmentsGridMolecule: React.FC<AttachmentsGridMoleculeProps> = ({
  sphereId,
  canEdit,
  isVisible = true,
  attachmentsEnabled = true,
  className,
}) => {
  const apiClients = useApiClientsOrNull();
  const effectiveSphereId =
    isVisible && attachmentsEnabled ? sphereId : undefined;

  const { attachments, isLoading, error, addFile, addLink, updateLink, remove } =
    useSphereAttachments({ sphereId: effectiveSphereId });

  const fetchSignedUrl = useCallback(
    async (attachmentId: string): Promise<string> => {
      if (!apiClients) {
        throw new Error('API clients not available');
      }
      const result = await getAttachmentSignedUrl(
        apiClients.graphClient,
        sphereId,
        attachmentId,
      );
      return result.signedUrl;
    },
    [apiClients, sphereId],
  );

  const handleAddFile = useCallback(
    async (file: File): Promise<void> => {
      await addFile(file);
    },
    [addFile],
  );

  const handleAddLink = useCallback(
    async (payload: AddLinkFormSubmitPayload): Promise<void> => {
      await addLink(payload);
    },
    [addLink],
  );

  const handleUpdateLink = useCallback(
    async (
      attachmentId: string,
      payload: UpdateLinkAttachmentPayload,
    ): Promise<void> => {
      await updateLink(attachmentId, payload);
    },
    [updateLink],
  );

  const handleRemove = useCallback(
    async (attachmentId: string): Promise<void> => {
      await remove(attachmentId);
    },
    [remove],
  );

  if (!attachmentsEnabled) return null;

  return (
    <AttachmentsGridMoleculeView
      canEdit={canEdit}
      attachments={attachments}
      isLoading={isLoading}
      error={error}
      onAddFile={handleAddFile}
      onAddLink={handleAddLink}
      onUpdateLink={handleUpdateLink}
      onRemove={handleRemove}
      fetchSignedUrl={apiClients ? fetchSignedUrl : undefined}
      className={className}
    />
  );
};

export default AttachmentsGridMolecule;
